import { useParams } from "react-router-dom";
import { useState } from "react";
import { useOfferPage, useStartRedemption } from "@/hooks/sms/useOfferPage";
import { PublicShell } from "@/pages/public/PublicShell";
import { Countdown } from "@/components/marketing/Countdown";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { OfferMethod } from "@/types/sms";

const METHOD_LABEL: Record<OfferMethod, string> = {
  phone: "Call in order",
  in_store: "Dine-in",
  online: "Order online",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

/** Only http(s) links render as a tappable "Open ordering site" — a stored
 * target_url of another scheme (e.g. javascript:) is refused rather than followed. */
function safeHttpUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

export default function OfferPage() {
  const { code = "" } = useParams<{ code: string }>();
  const { data, isLoading, refetch } = useOfferPage(code);
  const startRedemption = useStartRedemption(code);
  const [copied, setCopied] = useState(false);

  if (isLoading || !data) {
    return (
      <PublicShell>
        <div className="space-y-4">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      </PublicShell>
    );
  }

  if (data.state === "unavailable") {
    return (
      <PublicShell>
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <h1 className="text-lg font-semibold">This offer is no longer available</h1>
        </div>
      </PublicShell>
    );
  }

  if (data.state === "expired") {
    return (
      <PublicShell>
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <h1 className="text-lg font-semibold">This offer expired</h1>
          {data.valid_through && (
            <p className="mt-1 text-sm text-muted-foreground">This offer expired on {formatDate(data.valid_through)}.</p>
          )}
        </div>
      </PublicShell>
    );
  }

  const promotion = data.promotion!;
  const store = data.store!;

  const isRedeemed = data.state === "redeemed";
  const isStarted = data.state === "redemption_started" && data.started;

  return (
    <PublicShell>
      <div className="rounded-xl border border-border bg-card p-5">
        {promotion.image_url && (
          <img src={promotion.image_url} alt="" className="mb-4 h-40 w-full rounded-lg object-cover" />
        )}
        <h1 className="text-xl font-semibold">{promotion.title}</h1>
        {promotion.description && <p className="mt-1 text-sm text-muted-foreground">{promotion.description}</p>}
        <p className="mt-3 text-xs text-muted-foreground">{promotion.terms}</p>
        {data.valid_through && (
          <p className="mt-1 text-xs text-muted-foreground">Valid through {formatDate(data.valid_through)}</p>
        )}

        <div className="my-4 border-t border-border" />

        {isRedeemed ? (
          <div className="text-center">
            <p className="font-medium">Redeemed</p>
            <p className="mt-1 text-sm text-muted-foreground">This offer has already been used.</p>
          </div>
        ) : isStarted ? (
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">{promotion.redemption_instructions}</p>
            <button
              type="button"
              className="mx-auto block rounded-lg border border-dashed border-border px-4 py-2 font-mono text-2xl tracking-widest"
              onClick={() => {
                navigator.clipboard?.writeText(data.started!.code).catch(() => undefined);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {data.started!.code}
            </button>
            <p className="mt-1 text-xs text-muted-foreground">{copied ? "Copied!" : "Tap to copy"}</p>
            <div className="mt-4">
              <Countdown until={data.started!.expires_timer_at} onDone={() => refetch()} />
            </div>
            {data.started!.method === "phone" && store.phone && (
              <a href={`tel:${store.phone}`} className="mt-4 block">
                <Button className="h-12 w-full bg-action-primary">Call {store.phone}</Button>
              </a>
            )}
            {data.started!.method === "online" && safeHttpUrl(data.started!.url) && (
              <a href={safeHttpUrl(data.started!.url)!} target="_blank" rel="noreferrer" className="mt-4 block">
                <Button className="h-12 w-full bg-action-primary">Open ordering site</Button>
              </a>
            )}
          </div>
        ) : (
          <div>
            <p className="mb-2 text-sm font-medium">Select a redemption method</p>
            <div className="space-y-2">
              {(data.channels ?? []).map((c) => (
                <Button
                  key={c.channel}
                  className="h-12 w-full bg-action-primary"
                  disabled={startRedemption.isPending}
                  onClick={() => startRedemption.mutate({ method: c.channel })}
                >
                  {METHOD_LABEL[c.channel]}
                </Button>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              You&apos;ll have {Math.round((data.timer_minutes ?? 60) / 60) || 1} hour to purchase once you tap your
              redemption method above.
            </p>
          </div>
        )}
      </div>

      <footer className="mt-6 space-y-1 text-center text-xs text-muted-foreground">
        <p>{store.address}</p>
        {store.phone && <p>{store.phone}</p>}
        {store.hours && <p>{store.hours}</p>}
        <p>Reply STOP to any text to opt out.</p>
      </footer>
    </PublicShell>
  );
}
